// ═══════════════════════════════════════════════════════════════════════════════
// Unknown app route — dedicated 404 (do not silent-redirect to home)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { appButtonClassName } from '../components/ui/AppButton';
import { PLAY_BANNER_OFFSET_PADDING_CLASS } from '../lib/layoutChrome';

const NOT_FOUND_ROBOTS_META_ATTR = 'data-not-found-robots';

type NotFoundPageProps = {
  /** Override home link (e.g. admin portal dashboard). */
  homeTo?: string;
  /** Override home button label (admin portal is English-only). */
  homeLabel?: string;
  /** Override title (admin portal is English-only). */
  title?: string;
  /** Override body (admin portal is English-only). */
  body?: string;
};

export const NotFoundPage = ({
  homeTo = '/',
  homeLabel,
  title,
  body,
}: NotFoundPageProps) => {
  const { t } = useLanguage();
  const resolvedTitle = title ?? t('notFound.title');
  const resolvedBody = body ?? t('notFound.body');
  const resolvedHomeLabel = homeLabel ?? t('auth.goToHome');

  useEffect(() => {
    const existing = document.querySelector(`meta[${NOT_FOUND_ROBOTS_META_ATTR}="true"]`);
    if (existing) {
      return () => {
        existing.remove();
      };
    }

    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow');
    meta.setAttribute(NOT_FOUND_ROBOTS_META_ATTR, 'true');
    document.head.appendChild(meta);

    return () => {
      meta.remove();
    };
  }, []);

  return (
    <div className={`flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-950 ${PLAY_BANNER_OFFSET_PADDING_CLASS}`}>
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-gray-900">
        <div className="flex justify-center text-primary-600" aria-hidden="true">
          <svg
            className="h-16 w-16 shrink-0"
            viewBox="0 0 60 60"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0,0v12v2v46h60V14v-2V0H0z M20,39h-3v8c0,0.552-0.448,1-1,1s-1-0.448-1-1v-8H9c-0.552,0-1-0.448-1-1V27c0-0.552,0.448-1,1-1s1,0.448,1,1v10h5v-2c0-0.552,0.448-1,1-1s1,0.448,1,1v2h3c0.552,0,1,0.448,1,1S20.552,39,20,39z M36,41.5c0,3.584-2.916,6.5-6.5,6.5S23,45.084,23,41.5v-9c0-3.584,2.916-6.5,6.5-6.5s6.5,2.916,6.5,6.5V41.5z M51,39h-3v8c0,0.552-0.448,1-1,1s-1-0.448-1-1v-8h-6c-0.552,0-1-0.448-1-1V27c0-0.552,0.448-1,1-1s1,0.448,1,1v10h5v-2c0-0.552,0.448-1,1-1s1,0.448,1,1v2h3c0.552,0,1,0.448,1,1S51.552,39,51,39z M2,12V2h56v10H2z" />
            <polygon points="54.293,3.293 52,5.586 49.707,3.293 48.293,4.707 50.586,7 48.293,9.293 49.707,10.707 52,8.414 54.293,10.707 55.707,9.293 53.414,7 55.707,4.707" />
            <rect x="3" y="3" width="39" height="8" />
            <path d="M29.5,28c-2.481,0-4.5,2.019-4.5,4.5v9c0,2.481,2.019,4.5,4.5,4.5s4.5-2.019,4.5-4.5v-9C34,30.019,31.981,28,29.5,28z" />
          </svg>
        </div>
        <h1 className="mt-2 text-xl font-bold text-gray-900 dark:text-white" id="not-found-heading">
          {resolvedTitle}
        </h1>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{resolvedBody}</p>
        <Link
          to={homeTo}
          className={appButtonClassName({ variant: 'primary', fullWidth: true, className: 'mt-8' })}
          aria-labelledby="not-found-heading"
          tabIndex={0}
        >
          {resolvedHomeLabel}
        </Link>
      </div>
    </div>
  );
};
